/**
 * Hook for ENS resolution
 */

import { useState, useEffect } from 'react'
import { normalize } from 'viem/ens'
import { usePublicClient } from 'wagmi'
import type { Address } from 'viem'

interface ENSResolutionResult {
  address: Address | null
  isLoading: boolean
  error: string | null
}

/**
 * Resolve ENS name to address
 */
export function useENSResolution(ensName: string): ENSResolutionResult {
  const [address, setAddress] = useState<Address | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const publicClient = usePublicClient()

  useEffect(() => {
    if (!ensName || !ensName.includes('.eth')) {
      setAddress(null)
      setIsLoading(false)
      setError(null)
      return
    }

    const resolveENS = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const normalizedName = normalize(ensName)
        const resolvedAddress = await publicClient?.getEnsAddress({
          name: normalizedName,
        })

        if (resolvedAddress) {
          setAddress(resolvedAddress as Address)
        } else {
          setError('ENS name not found')
          setAddress(null)
        }
      } catch (err: any) {
        console.error('ENS resolution error:', err)
        setError(err.message || 'Failed to resolve ENS name')
        setAddress(null)
      } finally {
        setIsLoading(false)
      }
    }

    // Debounce ENS resolution
    const timeoutId = setTimeout(resolveENS, 500)
    return () => clearTimeout(timeoutId)
  }, [ensName, publicClient])

  return { address, isLoading, error }
}

